/*
 * ============================================================================
 * DATOS FICTICIOS — escenario de pruebas. Los agentes, departamentos,
 * usuarios y tenants de este simulador son inventados y NO describen el
 * producto ni lo desplegado. Solo se usa en desarrollo y en las pruebas del
 * Pórtico; en producción está apagado.
 * ============================================================================
 */

/**
 * Mock del descriptor de sesión — contrato v1.0
 * Ref: 02-contrato-descriptor-sesion.md §3
 *
 * Este mock se usa mientras R0 (kernel bootstrap) no esté disponible.
 * Cuando R0 esté vivo, src/api/client.js sustituye este objeto
 * por la respuesta real de POST /v1/session/bootstrap.
 *
 * MULTI-TENANT (US-58): el mismo build pinta cualquier tenant a partir de su
 * descriptor. Aquí conviven varios descriptores de ejemplo (DKtec, Koana) en
 * `MOCK_TENANTS`; NO hay ninguna rama de código por tenant — la UI se dirige
 * solo por estos datos. `MOCK_DESCRIPTOR` resuelve el tenant activo del mock
 * por `VITE_MOCK_TENANT` (default 'dktec'), lo que demuestra que servir a otro
 * tenant no requiere tocar código, solo cambiar el descriptor de entrada.
 *
 * CAMPO ADITIVO en `agents_visibles` (rediseño Home v2): `departamento`
 * (agrupa el equipo). Aditivo sobre v1.0, ratificado por dirección de
 * producto (Guo).
 *
 * IMÁGENES (GEN-11, decisión Guo 2026-07-17): el kernel NO emite URLs de
 * imagen. Las fotos de agente y el logo del tenant los resuelve el FRONT por
 * id (src/lib/images.js, mismo patrón que los colores) — por eso aquí no hay
 * `avatar_url` y `logo_url` viaja en null y el front lo ignora.
 *
 * DISPONIBILIDAD (Guo 2026-07-16): «de momento solo tenemos a Anne». La
 * ENTRADA solo muestra los agentes disponibles = `puede_iniciar_chat: true`.
 * Hoy, en DKtec, solo Anne está operativa; activar a otro es cambiar su flag
 * (en producción lo marca el kernel). El resto viaja con su departamento,
 * listo para aparecer — con su foto resuelta por id — sin tocar código.
 */

// ---------------------------------------------------------------------------
// Tenant 1 — DKtec (tenant fundador / piloto DK10)
// ---------------------------------------------------------------------------
const DKTEC = {
  contract_version: "1.0",

  session: {
    session_id: "sess-mock-dktec-001",
    issued_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(), // +1h
  },

  // Contrato §4 (US-69): mecanismo fijo, valores del tenant. DKtec usa los
  // defaults recomendados. El front aplica idle/absoluto; no decide la política.
  SESSION_POLICY: {
    access_ttl_min: 60,
    idle_timeout_min: 30,
    absolute_timeout_h: 8,
    refresh: "silent-rebootstrap",
  },

  tenant: {
    id: "dktec",
    nombre: "DKtec",
    branding: {
      // El kernel no emite logo: el front lo resuelve por tenant.id
      // (/logos/dktec.svg, lib/images.js) e IGNORA este campo (GEN-11).
      logo_url: null,
      color_primario: "#007A33",
      // Jost es la tipografía de plataforma DKtec (Gabinete #280, auto-alojada).
      tipografia: "'Jost', 'Segoe UI', system-ui, sans-serif",
    },
    dominios_activos: ["ORIGEN", "KOANA"],
  },

  user: {
    id: "user-nombre-001",
    display_name: "Nombre Apellido",
    // Identidad Cognito que se delega a R0 (coherente con el seed de US-26).
    // Presentacional: el Login la muestra como «identidad delegada» en mock.
    email: "nombre@ejemplo.test",
    // Enum canónico US-26: usuario|champion|admin. OWNER (tenant fundador) se
    // mapea a admin (decisión Guo 2026-07-07). El front no condiciona por rol:
    // la visibilidad del panel la gobierna capabilities.menu (R-PT-02).
    rol: "admin",
    idioma: "es",
  },

  capabilities: {
    // data-driven: Pórtico pinta este menú tal cual (R-PT-02)
    menu: [
      { id: "chats",    label: "Conversaciones", icon: "chat",    visible: true,  enabled: true,  target: "/chats"    },
      // GEN-PX-022: Personas · Agentes · Saber, ítems propios que el kernel emite a TODOS los perfiles.
      { id: "personas", label: "Personas",        icon: "personas", visible: true, enabled: true,  target: "/personas" },
      // GEN-PX-023: al administrador, Agentes lleva Equipo y Perfiles (hijos que decide el kernel); al resto, sin hijos.
      { id: "agentes",  label: "Agentes",         icon: "agents",  visible: true,  enabled: true,  target: "/agentes", hijos: [{ id: "equipo", label: "Equipo" }, { id: "agentes-permisos", label: "Perfiles" }] },
      // GEN-PX-018: Estrategia para todos, con la cabecera que emite el kernel (presidida por Jorge).
      { id: "estrategia", label: "Estrategia", icon: "estrategia", visible: true, enabled: true, target: "/estrategia",
        cabecera: {
          agente: { id: "jorge", nombre: "Jorge", rol: "Recursos Humanos", foto_url: "", avatar_url: "" },
          titulo: "Estrategia",
          texto: "Aquí verás los propósitos de DKtec y cómo encaja tu trabajo en ellos. Mientras llegan, cuéntame qué te preocupa de la estrategia.",
          boton: { etiqueta: "Habla con Jorge sobre la estrategia", agente_id: "jorge" },
        } },
      { id: "saber",    label: "Saber",           icon: "saber",   visible: true,  enabled: true,  target: "/saber"    },
      // GEN-PX-016/023: Auditoría, con las entradas (hijos) que el kernel da a cada perfil (admin: Detenciones y Saber; champion: Saber; usuario: nada).
      { id: "auditoria", label: "Auditoría", icon: "auditoria", visible: true, enabled: true, target: "/auditoria", hijos: [{ id: "detenciones", label: "Detenciones" }, { id: "saber", label: "Saber" }],
        cabecera: {
          agente: { id: "jorge", nombre: "Jorge", rol: "Recursos Humanos", foto_url: "", avatar_url: "" },
          titulo: "Auditoría · lo que ha pasado en DKtec",
          texto: "En los últimos 7 días: 2 detenciones y 5 cambios en Saber",
          cifras: [{ texto: "2 detenciones", seccion: "detenciones" }, { texto: "5 cambios en Saber", seccion: "saber" }],
        } },
      // GEN-22 #323: visor de ficheros de la ZFT (eje A del owner). El kernel
      // solo lo emite al owner; el front únicamente lo pinta (R-PT-02/R-PT-04).
      { id: "ficheros", label: "Ficheros",        icon: "files",   visible: true,  enabled: true,  target: "/ficheros" },
      { id: "informes", label: "Informes",         icon: "reports", visible: true,  enabled: false, target: "/informes" },
      { id: "areas",    label: "Áreas",            icon: "areas",   visible: true,  enabled: false, target: "/areas"    },
      // US-26: panel de administración. El kernel SOLO incluye este ítem si el
      // usuario es admin; el front únicamente lo pinta (R-PT-02/R-PT-04).
      { id: "admin",    label: "Administración",   icon: "admin",   visible: true,  enabled: true,  target: "/admin",
        // GEN-PX-023: las cinco entradas de la instalación, las que el carril contextual lista (las decide el kernel).
        hijos: [{ id: "herramientas", label: "Herramientas" }, { id: "modelos", label: "Modelos" }, { id: "libro-de-estilo", label: "Marca y estilo" }, { id: "coste", label: "Coste" }, { id: "salud", label: "Salud" }] },
      // GEN-AG-001: Mi equipo (fichas de agente). Solo admin, como Ficheros.
      { id: "equipo",   label: "Mi equipo",        icon: "equipo",  visible: true,  enabled: true,  target: "/equipo"   },
      // GEN-179a: mapa de permisos de los agentes (solo lectura). Solo admin.
      { id: "agentes-permisos", label: "Agentes", icon: "permisos", visible: true, enabled: true, target: "/admin/agentes" },
      // GEN-TL-001 / GEN-MO-016: catálogo de herramientas y coste. Solo admin (GEN-PX-017: pestañas de Admin).
      { id: "herramientas", label: "Herramientas", icon: "herramientas", visible: true, enabled: true, target: "/admin/herramientas" },
      { id: "coste", label: "Coste", icon: "coste", visible: true, enabled: true, target: "/coste" },
      // GEN-MO-009: catálogo y política de modelos. Solo admin.
      { id: "modelos", label: "Modelos", icon: "modelos", visible: true, enabled: true, target: "/admin/modelos" },
      // GEN-MO-018: salud de la instalación. Solo admin.
      { id: "salud", label: "Salud", icon: "salud", visible: true, enabled: true, target: "/admin/salud" },
      // GEN-PX-001: «Marca y estilo» (libro de estilo del tenant). Solo admin.
      { id: "libro-de-estilo", label: "Marca y estilo", icon: "estilo", visible: true, enabled: true, target: "/admin/marca" },
      // GEN-PX-002: «Mi empresa» > Conocimiento > KBs (lo emite el kernel a admin y champion). Alimenta «Saber» del raíl.
      { id: "empresa:conocimiento:kbs", label: "KBs", icon: "kbs", visible: true, enabled: true, target: "/empresa/conocimiento/kbs" },
      // GEN-76 T7: un ítem por mecanismo VISIBLE, resuelto por código. El kernel
      // solo lo emite a quien tiene el scope de entrada; el front lo pinta y la
      // vista la resuelve el registro por prefijo (`mecanismo:`), sin condicional
      // por mecanismo. `contract_version` NO sube: es un ítem más de una lista
      // abierta por diseño, igual que «Administración» y «Ficheros».
      { id: "mecanismo:MEC-DEMO", label: "Mecanismo de demostración", descripcion: "Fichas de ejemplo para probar el visor y sus acciones.", icon: "mecanismo", visible: true, enabled: true, target: "/mecanismos/MEC-DEMO" },
    ],
    acciones_permitidas: [
      "crear_chat",
      "enviar_mensaje",
      "aprobar_accion",
      "gestionar_usuarios",
      "mec:MEC-DEMO:admin",
      "mec:MEC-DEMO:proponer",
    ],
  },

  // Equipo IA de DKtec — roster oficial (19 agentes / 6 departamentos). Todos
  // traen departamento. Sin foto en el mock (GEN-AG-006: la foto es la de la
  // ficha en vigor que sirve el kernel), así que se pintan las iniciales.
  // `puede_iniciar_chat` marca quién está OPERATIVO: hoy solo Anne; el resto
  // entra en servicio activando su flag (en producción lo hace el kernel). La
  // entrada muestra solo los disponibles.
  agents_visibles: [
    // — Dirección —
    {
      id: "anne",
      nombre: "Anne",
      rol: "Orquestadora",
      departamento: "Dirección",
      puede_iniciar_chat: true,
      descripcion: "Dirige el equipo IA. Si no sabes a quién preguntar, empieza aquí.",
    },
    {
      id: "julia",
      nombre: "Julia",
      rol: "Investigadora Senior",
      departamento: "Dirección",
      puede_iniciar_chat: false,
      descripcion: "Investiga a fondo y sintetiza hallazgos con rigor.",
    },
    {
      id: "jorge",
      nombre: "Jorge",
      rol: "Recursos Humanos",
      departamento: "Dirección",
      puede_iniciar_chat: false,
      descripcion: "Personas, roles y organización del equipo.",
    },
    {
      id: "melissa",
      nombre: "Melissa",
      rol: "Product Owner",
      departamento: "Dirección",
      puede_iniciar_chat: false,
      descripcion: "Prioriza el backlog y cierra el alcance de cada entrega.",
    },
    {
      id: "erin",
      nombre: "Erin",
      rol: "Especialista Legal",
      departamento: "Dirección",
      puede_iniciar_chat: false,
      descripcion: "Revisa cumplimiento, contratos y riesgo legal.",
    },

    // — DIKW Office —
    {
      id: "jimmy",
      nombre: "Jimmy",
      rol: "Ingeniero de Conocimiento",
      departamento: "DIKW Office",
      puede_iniciar_chat: false,
      descripcion: "Gestiona la base de conocimiento empresarial.",
    },
    {
      id: "cassie",
      nombre: "Cassie",
      rol: "Estratega de IA",
      departamento: "DIKW Office",
      puede_iniciar_chat: false,
      descripcion: "Diseña la estrategia de transformación IA-Nativa.",
    },
    {
      id: "seo",
      nombre: "Seo",
      rol: "CDAIO",
      departamento: "DIKW Office",
      puede_iniciar_chat: false,
      descripcion: "Chief Digital & AI Officer. Acompaña la transformación.",
    },
    {
      id: "max",
      nombre: "Max",
      rol: "Curador del DTM",
      departamento: "DIKW Office",
      puede_iniciar_chat: false,
      descripcion: "Cura el Digital Twin Model y el gobierno del dato.",
    },

    // — Ventas —
    {
      id: "linda",
      nombre: "Linda",
      rol: "Directora Comercial",
      departamento: "Ventas",
      puede_iniciar_chat: false,
      descripcion: "Estrategia comercial y relación con clientes.",
    },
    {
      id: "nancy",
      nombre: "Nancy",
      rol: "Especialista en Presentaciones",
      departamento: "Ventas",
      puede_iniciar_chat: false,
      descripcion: "Prepara propuestas y presentaciones de impacto.",
    },

    // — Operaciones —
    {
      id: "nick",
      nombre: "Nick",
      rol: "Director de Operaciones IA",
      departamento: "Operaciones",
      puede_iniciar_chat: false,
      descripcion: "Coordina la operación de los agentes en producción.",
    },
    {
      id: "grace",
      nombre: "Grace",
      rol: "Consultora e Ingeniera ISCOBOL",
      departamento: "Operaciones",
      puede_iniciar_chat: false,
      descripcion: "Moderniza y da soporte a sistemas ISCOBOL heredados.",
    },
    {
      id: "jack",
      nombre: "Jack",
      rol: "Consultor e Ingeniero RM/COBOL",
      departamento: "Operaciones",
      puede_iniciar_chat: false,
      descripcion: "Consultoría y desarrollo sobre RM/COBOL.",
    },

    // — Tecnología & Sistemas —
    {
      id: "werner",
      nombre: "Werner",
      rol: "CTO / Head of IT",
      departamento: "Tecnología & Sistemas",
      puede_iniciar_chat: false,
      descripcion: "Decisiones técnicas y arquitectura de sistemas.",
    },
    {
      id: "vint",
      nombre: "Vint",
      rol: "Especialista en Infraestructura",
      departamento: "Tecnología & Sistemas",
      puede_iniciar_chat: false,
      descripcion: "Redes, despliegue e infraestructura cloud.",
    },
    {
      id: "linus",
      nombre: "Linus",
      rol: "Ingeniero de Desarrollo Full-Stack",
      departamento: "Tecnología & Sistemas",
      puede_iniciar_chat: false,
      descripcion: "Construye el producto de principio a fin.",
    },

    // — Control —
    {
      id: "amy",
      nombre: "Amy",
      rol: "CFO",
      departamento: "Control",
      puede_iniciar_chat: false,
      descripcion: "Finanzas, presupuesto y control de gestión.",
    },
    {
      id: "satya",
      nombre: "Satya",
      rol: "Jefe de Backoffice",
      departamento: "Control",
      puede_iniciar_chat: false,
      descripcion: "Backoffice, administración y operativa Odoo.",
    },
  ],

  // Resumen de chats — la historia completa se pide bajo demanda (R-PT-06)
  chats: [
    {
      chat_id: "chat-001",
      titulo: "Maduración del Kernel",
      de_que_va: "Diseño del contrato descriptor, decisiones de transporte SSE/REST y requisitos de Pórtico.",
      agentes_en_chat: [
        { nombre: "Cassie", proposito: "diseño del kernel y contrato" },
        { nombre: "Werner", proposito: "decisiones de transporte" },
      ],
      updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    },
    {
      chat_id: "chat-002",
      titulo: "Estrategia KOANA — onboarding",
      de_que_va: "Preparación del proyecto ORIGEN para el onboarding de Koana y primeras acciones de colaboración.",
      agentes_en_chat: [
        { nombre: "Cassie", proposito: "estrategia de onboarding" },
        { nombre: "Seo",    proposito: "acompañamiento transformación" },
      ],
      updated_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      chat_id: "chat-003",
      titulo: "Limpieza de Odoo — tareas pendientes",
      de_que_va: "Revisión del backlog de limpieza del ERP y priorización de la deuda técnica.",
      agentes_en_chat: [
        { nombre: "Satya", proposito: "acceso y operación Odoo" },
        { nombre: "Nick",  proposito: "eficiencia operativa" },
      ],
      updated_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ],
}

// ---------------------------------------------------------------------------
// Tenant 2 — Koana (segundo tenant; prueba de multi-tenant, US-58)
// Deliberadamente DISTINTO en todo lo data-driven: marca, tipografía, menú
// (sin panel admin — este usuario no es admin), agentes y chats. Ni una sola
// línea de código de Pórtico cambia entre este tenant y DKtec: solo el
// descriptor. Sus agentes y su logo no tienen assets en el front (nadia/omar
// -> iniciales; logo -> insignia «K»), lo que además ejercita ese fallback.
// ---------------------------------------------------------------------------
const KOANA = {
  contract_version: "1.0",

  session: {
    session_id: "sess-mock-koana-001",
    issued_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
  },

  // Koana endurece su política (idle más corto, vida máxima menor): misma UI,
  // distinto binding de tenant. Evidencia de que la política es data-driven.
  SESSION_POLICY: {
    access_ttl_min: 60,
    idle_timeout_min: 15,
    absolute_timeout_h: 4,
    refresh: "silent-rebootstrap",
  },

  tenant: {
    id: "koana",
    nombre: "Koana",
    branding: {
      logo_url: null,
      color_primario: "#6B3FA0",           // morado — distinto del verde DKtec
      tipografia: "Georgia, 'Times New Roman', serif",
    },
    dominios_activos: ["ORIGEN"],
  },

  user: {
    id: "user-koana-al",
    display_name: "Alicia Prat",
    email: "alicia.prat@koana.es",
    rol: "usuario",                          // no admin -> el kernel no emite el ítem admin
    idioma: "es",
  },

  capabilities: {
    // Menú distinto: sin panel admin, "Informes" habilitado aquí. Data-driven.
    menu: [
      { id: "chats",    label: "Conversaciones", icon: "chat",    visible: true,  enabled: true,  target: "/chats"    },
      { id: "personas", label: "Personas",        icon: "personas", visible: true, enabled: true,  target: "/personas" },
      { id: "agentes",  label: "Asistentes",      icon: "agents",  visible: true,  enabled: true,  target: "/agentes"  },
      { id: "saber",    label: "Saber",           icon: "saber",   visible: true,  enabled: true,  target: "/saber"    },
      { id: "informes", label: "Informes",         icon: "reports", visible: true,  enabled: true,  target: "/informes" },
    ],
    acciones_permitidas: ["crear_chat", "enviar_mensaje"],
  },

  agents_visibles: [
    {
      id: "anne",
      nombre: "Anne",
      rol: "Orquestadora",
      departamento: "Dirección",
      puede_iniciar_chat: true,
      descripcion: "Coordina a los asistentes de Koana.",
    },
    {
      id: "nadia",
      nombre: "Nadia",
      rol: "Analista de Datos",
      departamento: "Analítica",
      puede_iniciar_chat: true,
      descripcion: "Explora los datos operativos de Koana.",
    },
    {
      id: "omar",
      nombre: "Omar",
      rol: "Soporte de Producto",
      departamento: "Soporte",
      puede_iniciar_chat: false,
      descripcion: "Atiende dudas de producto. Próximamente disponible para chat.",
    },
  ],

  chats: [
    {
      chat_id: "kchat-001",
      titulo: "Arranque de Koana en ORIGEN",
      de_que_va: "Primeros pasos del onboarding de Koana y objetivos del piloto.",
      agentes_en_chat: [
        { nombre: "Anne",  proposito: "orquestación del arranque" },
        { nombre: "Nadia", proposito: "diagnóstico de datos" },
      ],
      updated_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    },
    {
      chat_id: "kchat-002",
      titulo: "Informe de operaciones — borrador",
      de_que_va: "Preparación del primer informe operativo con los datos disponibles.",
      agentes_en_chat: [
        { nombre: "Nadia", proposito: "análisis de operaciones" },
      ],
      updated_at: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(),
    },
  ],
}

// ---------------------------------------------------------------------------
// Registro de tenants de mock. La clave es el id de tenant.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Escenarios de DESARROLLO para las tarjetas del equipo (GEN-PX-010): el mismo
// DKtec con los agentes operativos y fotos (`src/mock/fotos/*.svg` (solo dev), ficticias).
// Anne trae MINIATURA (recorte de cara) y FOTO distintas, como una ficha real
// con miniatura; el resto, solo foto (avatar_url == foto_url, como el kernel).
//   VITE_MOCK_TENANT=equipo-demo     -> 8 agentes (una sola fila de columnas)
//   VITE_MOCK_TENANT=equipo-demo-19  -> 19 agentes (tope de 10 + «Ver todo el equipo»)
// ---------------------------------------------------------------------------
const LIDERES_DEMO = new Set(["anne", "seo", "linda", "nick", "werner", "amy"])
const foto = (id) => `/mock-fotos/${id}-foto.svg`
const conFotos = (a) => ({
  ...a,
  puede_iniciar_chat: true,
  lidera: LIDERES_DEMO.has(a.id),
  avatar_url: a.id === "anne" ? "/mock-fotos/anne-miniatura.svg" : foto(a.id),
  foto_url: foto(a.id),
})
const diasAtras = (d) => new Date(Date.now() - d * 24 * 60 * 60 * 1000).toISOString()
const chatDemo = (n, dias, ...nombres) => ({
  chat_id: `demo-${n}`,
  titulo: `Conversación ${n}`,
  de_que_va: "Escenario de desarrollo.",
  agentes_en_chat: nombres.map(nombre => ({ nombre, proposito: "" })),
  updated_at: diasAtras(dias),
})
const CHATS_DEMO = [
  chatDemo(1, 0.1, "Anne"), chatDemo(2, 1, "Anne", "Seo"), chatDemo(3, 2, "Anne"),
  chatDemo(4, 3, "Seo"), chatDemo(5, 4, "Vint"), chatDemo(6, 5, "Anne", "Jimmy"),
  chatDemo(7, 6, "Werner", "Linus"), chatDemo(8, 9, "Nick"),
]
const demoDe = (ids) => ({
  ...DKTEC,
  agents_visibles: DKTEC.agents_visibles.filter(a => !ids || ids.includes(a.id)).map(conFotos),
  chats: CHATS_DEMO,
})

export const MOCK_TENANTS = {
  dktec: DKTEC,
  koana: KOANA,
  "equipo-demo": demoDe(["anne", "seo", "jimmy", "cassie", "linda", "werner", "vint", "linus"]),
  "equipo-demo-19": demoDe(null),
}

// Devuelve el descriptor de mock de un tenant (default 'dktec' si no existe).
export function getMockDescriptor(tenantId) {
  return MOCK_TENANTS[tenantId] ?? DKTEC
}

// Tenant activo del mock: seleccionable por entorno SIN tocar código (US-58).
// En dev: `VITE_MOCK_TENANT=koana npm run dev`. Default: DKtec.
const ACTIVE_TENANT = import.meta.env?.VITE_MOCK_TENANT || 'dktec'

// Descriptor por defecto que consume el cliente en modo mock.
export const MOCK_DESCRIPTOR = getMockDescriptor(ACTIVE_TENANT)
